import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, Checkbox, IconButton } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import { FS } from "@plannel/utils/font-util";

/*
 * DW 대시보드 공용 — 카드 레이아웃 개인화 모달(표시 여부 + 순서).
 * 저장 위치는 호출측이 결정(onSave) — 보통 z_p13n 의 layout 상위 키(useDashboardP13n.saveLayout).
 * 셸 = BaseConfigModal(표준). 카드가 여러 대시보드에서 재사용되도록 도메인 무관.
 *
 * props
 *   open, onClose : 모달 열림/닫기
 *   cards         : [{ key, label }] — 대시보드의 전체 카드(라벨은 이미 번역된 문자열)
 *   layout        : { order:[cardKey], hidden:{ key:bool } }
 *   onSave(next)  : 저장 콜백(편집본 layout 전달)
 */
const DashboardLayoutModal = ({ open, onClose, cards = [], layout, onSave }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(layout);
  useEffect(() => { if (open) setDraft(layout); }, [open, layout]);

  const order = draft?.order ?? cards.map((c) => c.key);
  const hidden = draft?.hidden ?? {};
  const labelOf = (key) => cards.find((c) => c.key === key)?.label ?? key;

  const toggleVisible = (key) =>
    setDraft((d) => ({ ...d, order, hidden: { ...hidden, [key]: !hidden[key] } }));

  // i 번째 카드를 dir(-1 위 / +1 아래)로 이동
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= order.length) return;
    const next = [...order];
    [next[i], next[j]] = [next[j], next[i]];
    setDraft((d) => ({ ...d, order: next, hidden }));
  };

  return (
    <BaseConfigModal open={open} onClose={onClose} title={t("invLayoutConfig")} onSave={() => onSave(draft)} size="sm">
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {order.map((key, i) => (
          <Box key={key} sx={{
            display: "flex", alignItems: "center", gap: 0.5, py: 0.25,
            borderBottom: i < order.length - 1 ? 1 : 0, borderColor: "divider",
          }}>
            <Checkbox
              checked={!hidden[key]}
              onChange={() => toggleVisible(key)}
              size="small"
              sx={{ p: 0.3 }}
            />
            <Typography sx={{ flex: 1, fontSize: FS.TITLE, color: hidden[key] ? "text.secondary" : "text.primary" }}>
              {labelOf(key)}
            </Typography>
            <IconButton size="small" disabled={i === 0} onClick={() => move(i, -1)} sx={{ p: 0.25 }}>
              <ArrowUpwardIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton size="small" disabled={i === order.length - 1} onClick={() => move(i, 1)} sx={{ p: 0.25 }}>
              <ArrowDownwardIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        ))}
      </Box>
    </BaseConfigModal>
  );
};

export default DashboardLayoutModal;
